import { Span } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { type LimitedBasketSizeConfiguration } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  convertNumberToWord,
  isNumberConvertableToWord,
} from "@src/appV2/lib/utils/convertNumberToWord";
import { Button } from "@src/appV2/redesign/components/Button";
import { TIERS_FULL_PATH } from "@src/appV2/redesign/Tiers/paths";
import { type Worker } from "@src/appV2/Worker/api/types";
import pluralize from "pluralize";
import { useHistory } from "react-router-dom";

import { ShiftBottomSheetBookabilityCriteriaCard } from "./BookabilityCriteriaCard";
import { BookabilityCriteriaCardTitleText } from "./BookabilityCriteriaCardTitleText";

interface ShiftBottomSheetLimitedTierShiftCardProps {
  leadTimeThresholdExceeded: boolean;
  basketSizeLimitExceeded: boolean;
  worker: Worker;
}

function getLeadTimeThreshold(
  substage: Worker["substage"],
  limitedBasketSizeConfig: LimitedBasketSizeConfiguration
) {
  if (!limitedBasketSizeConfig.enabled) {
    return undefined;
  }

  const { leadTimeThresholdInHrs } = limitedBasketSizeConfig;

  return substage?.accessMetadata?.leadTimeThresholdInHrs ?? leadTimeThresholdInHrs;
}

function getBasketSizeLimit(
  substage: Worker["substage"],
  limitedBasketSizeConfig: LimitedBasketSizeConfiguration
) {
  if (!limitedBasketSizeConfig.enabled) {
    return undefined;
  }

  const { basketSize } = limitedBasketSizeConfig;
  return substage?.accessMetadata?.basketSize ?? basketSize;
}

function getLimitedReason(
  leadTimeThresholdExceeded: boolean,
  basketSizeLimitExceeded: boolean,
  leadTimeThreshold: number,
  basketSizeLimit: number
) {
  if (leadTimeThresholdExceeded) {
    return `book shifts that start within the next ${leadTimeThreshold} ${pluralize(
      "hour",
      leadTimeThreshold
    )}`;
  }

  if (basketSizeLimitExceeded && isNumberConvertableToWord(basketSizeLimit)) {
    const word = convertNumberToWord(basketSizeLimit);
    return `have ${word} upcoming ${pluralize("shift", basketSizeLimit)} at a time`;
  }

  return undefined;
}

export function ShiftBottomSheetLimitedTierShiftCard(
  props: ShiftBottomSheetLimitedTierShiftCardProps
) {
  const history = useHistory();
  const { leadTimeThresholdExceeded, basketSizeLimitExceeded, worker } = props;

  const { substage, userId: workerId } = worker;

  const basketSizeConfig = useCbhFlag(CbhFeatureFlag.BASKET_SIZE_LIMIT_CONFIGURATION, {
    defaultValue: { limited: { enabled: false }, warning: { enabled: false } },
  });

  const { limited } = basketSizeConfig;

  const leadTimeThreshold = getLeadTimeThreshold(substage, limited);
  const basketSizeLimit = getBasketSizeLimit(substage, limited);

  if (!isDefined(leadTimeThreshold) || !isDefined(basketSizeLimit)) {
    return null;
  }

  const limitedReason = getLimitedReason(
    leadTimeThresholdExceeded,
    basketSizeLimitExceeded,
    leadTimeThreshold,
    basketSizeLimit
  );

  if (!isDefined(limitedReason)) {
    return null;
  }

  return (
    <ShiftBottomSheetBookabilityCriteriaCard
      intent="limited"
      title={
        <BookabilityCriteriaCardTitleText>
          You can only {limitedReason} while{" "}
          <Span
            sx={(theme) => ({
              color: theme.palette.tiers?.limited.text,
              fontWeight: theme.typography.fontWeightMedium,
            })}
          >
            Limited
          </Span>
        </BookabilityCriteriaCardTitleText>
      }
      description="Work your way up to Silver to unlock unlimited bookings"
      illustrationType="tiers-shift-limited"
      footer={
        <Button
          size="medium"
          role="button"
          sx={{ width: "100%" }}
          onClick={() => {
            history.push(TIERS_FULL_PATH);
            logEvent(APP_V2_USER_EVENTS.TIERS_BOOKING_CARD_LEARN_MORE_CLICKED, {
              workerId,
              workerTier: "LIMITED",
            });
          }}
        >
          Learn More
        </Button>
      }
    />
  );
}
